/**
 * Route guard logic for RBAC Route.
 * RBAC RULE: this file protects navigation behavior before a route is activated or matched.
 */
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { inject } from '@angular/core';

import { UiToastService } from '@shared/ui';
import { AuthService } from '../../features/login/auth.service';
import { TokenService } from '../services/token.service';
import { AccessKey } from './access-control.config';
import { PermissionsService } from './permissions.service';

export const canActivateAuth: CanActivateFn = () => {
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const router = inject(Router);

  // Require both a verified user object and a live access token.
  // The APP_INITIALIZER calls /auth/me on every load; if it fails, clearSession()
  // sets currentUser to null and clears the token, so both checks fail together.
  if (!authService.currentUser() || !tokenService.getAccessToken()) {
    return router.createUrlTree(['/login']);
  }

  return true;
};

/**
 * BUSINESS LOGIC: canMatchByAccess
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function canMatchByAccess(key: AccessKey): CanMatchFn {
  return () => {
    const authService = inject(AuthService);
    const permissionsService = inject(PermissionsService);
    const toastService = inject(UiToastService);
    const router = inject(Router);
    const user = authService.currentUser();

    if (!user) {
      return router.createUrlTree(['/login']);
    }

    if (!permissionsService.canAccess(key)) {
      toastService.error('Access denied', 'You do not have permission to access this resource.');
      return router.createUrlTree(['/dashboard']);
    }

    return true;
  };
}

/**
 * BUSINESS LOGIC: canMatchAgencyScopedAccess
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function canMatchAgencyScopedAccess(key: AccessKey): CanMatchFn {
  return (_route, segments) => {
    const authService = inject(AuthService);
    const permissionsService = inject(PermissionsService);
    const toastService = inject(UiToastService);
    const router = inject(Router);
    const user = authService.currentUser();

    if (!user) {
      return router.createUrlTree(['/login']);
    }

    if (!permissionsService.canAccess(key)) {
      toastService.error('Access denied', 'You do not have permission to access this resource.');
      return router.createUrlTree(['/dashboard']);
    }

    if (user.role !== 'supervisor') {
      return true;
    }

    const requestedAgencyId = segments[0]?.path;

    if (requestedAgencyId === 'me') {
      return true;
    }

    if (!user.assignedAgencyId) {
      toastService.error('Access denied', 'No agency is assigned to your account.');
      return router.createUrlTree(['/dashboard']);
    }

    if (requestedAgencyId && requestedAgencyId !== user.assignedAgencyId) {
      toastService.error('Access denied', 'You can only access your assigned agency.');
      return router.createUrlTree(['/agencies', 'me']);
    }

    return true;
  };
}

export const canMatchAgencyHome: CanMatchFn = () => {
  const authService = inject(AuthService);
  const permissionsService = inject(PermissionsService);
  const toastService = inject(UiToastService);
  const router = inject(Router);
  const user = authService.currentUser();

  if (!user) {
    return router.createUrlTree(['/login']);
  }

  if (user.role === 'supervisor' && permissionsService.canAccess('agencyDetail')) {
    return router.createUrlTree(['/agencies', 'me']);
  }

  if (!permissionsService.canAccess('agenciesList')) {
    toastService.error('Access denied', 'You do not have permission to access this resource.');
    return router.createUrlTree(['/dashboard']);
  }

  return true;
};
